import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import HeaderFooterLayout from '../components/layouts/HeaderFooterLayout'
import Table from '../components/Table'
import styles from './RecordingsJobs.module.css'
import api from '../api'
import Card from '../components/cards/Card'
import typo from '../typography.module.css'
import StatusBadge from '../components/StatusBadge'
import { Helmet, HelmetProvider } from 'react-helmet-async'
import ProgressBar from '../components/ProgressBar'
import MeatBallsMenu from '../components/MeatBallsMenu'
import { formatDateV2, formatFileSize, formatSeconds, formatSecondsDuration, formatTimeV2 } from '../utils'
import Dialog from '../components/dialogs/Dialog'
import AlertDialog from '../components/dialogs/AlertDialog'
import { ReactComponent as ArrowIcon } from '../assets/images/icons/ic-arrow-down.svg'
import { ReactComponent as CancelIcon } from '../assets/images/icons/ic-close.svg'
import { ReactComponent as DeleteIcon } from '../assets/images/icons/ic-delete.svg'
import { ReactComponent as RetryIcon } from '../assets/images/icons/ic-retry.svg'
import { ReactComponent as PlayIcon } from '../assets/images/icons/ic-play.svg'
import { ReactComponent as PauseIcon } from '../assets/images/icons/ic-pause.svg'
import { ReactComponent as SubtitlesIcon } from '../assets/images/icons/ic-subtitles.svg'
import Pagination from '../components/Pagination'
import useWindowDimensions from '../common/hooks/useWindowDimensions'
import Select from '../components/Select'
import TextInput from '../components/TextInput'
import { ReactComponent as FilterIcon } from '../assets/images/icons/ic-filter.svg'
import Button from '../components/Button'
import { ButtonStatus } from '../common/constants'
import { useSearchParams } from 'react-router-dom'

const JobBadge = ({ name, color }) => <StatusBadge appearance="soft" label={name || 'Non disponibile'} color={color} />

const RecordingsJobs = () => {
    const [searchParams] = useSearchParams()

    const { width } = useWindowDimensions()
    const [filtersOpen, setFiltersOpen] = useState(false)
    const [jobSearch, setJobSearch] = useState('')
    const [debouncedJobSearch, setDebouncedJobSearch] = useState('')
    useEffect(() => {
        const timer = setTimeout(() => { setPage(0); setDebouncedJobSearch(jobSearch.trim()) }, 350)
        return () => clearTimeout(timer)
    }, [jobSearch])
    const [jobs, setJobs] = useState([])
    const [count, setCount] = useState(0)
    const [workers, setWorkers] = useState([])
    const [loading, setLoading] = useState(false)
    const [loadingWorkers, setLoadingWorkers] = useState(false)
    const [page, setPage] = useState(0)
    const [per] = useState(25)
    const [jobStatusFilter, setJobStatusFilter] = useState(() => searchParams.get('status') === 'failed' ? 'failed' : null)
    const hasLoadedRef = useRef(false)
    const jobsRequestRef = useRef(null)
    const [jobFilters, setJobFilters] = useState({ from_date: "", to_date: "", course_id: null, edition_id: null, module_id: null })
    const [filterCourses, setFilterCourses] = useState([])
    const [filterEditions, setFilterEditions] = useState([])
    const [filterSearch, setFilterSearch] = useState({})
    const [loadingFilterOptions, setLoadingFilterOptions] = useState({})
    const [jobsError, setJobsError] = useState("")
    const [filterError, setFilterError] = useState("")
    const hasLoadedWorkersRef = useRef(false)
    const isFetchingWorkersRef = useRef(false)
    const [selectedJob, setSelectedJob] = useState(null)
    const [lessonsToProcess, setLessonsToProcess] = useState([])
    const [loadingLessons, setLoadingLessons] = useState(false)
    const [lessonsToProcessSubtitles, setLessonsToProcessSubtitles] = useState([])
    const [loadingLessonsSubtitles, setLoadingLessonsSubtitles] = useState(false)
    const [nowTs, setNowTs] = useState(Date.now())
    const [jobActionDialog, setJobActionDialog] = useState({ open: false, type: null, job: null })
    const [workerActionDialog, setWorkerActionDialog] = useState({ open: false, worker: null })
    const [subtitleActionDialog, setSubtitleActionDialog] = useState({ open: false, lesson: null })
    const [bulkSubtitleDialogOpen, setBulkSubtitleDialogOpen] = useState(false)
    const [bulkSubtitleStatus, setBulkSubtitleStatus] = useState(null)

    const jobStatusOptions = useMemo(() => ([
        { id: 'queued', name: 'In coda', value: 'queued' },
        { id: 'processing', name: 'In elaborazione', value: 'processing' },
        { id: 'retrying', name: 'In retry', value: 'retrying' },
        { id: 'completed', name: 'Completato', value: 'completed' },
        { id: 'failed', name: 'Fallito', value: 'failed' },
        { id: 'cancelled', name: 'Annullato', value: 'cancelled' }
    ]), [])

    const getLessonsToProcess = useCallback(async () => {
        setLoadingLessons(true)
        try {
            const lessons = await api.get('/admin/lessons/process')
            setLessonsToProcess(lessons)
        } catch (error) {
            console.error(error)
        } finally {
            setLoadingLessons(false)
        }
    }, [])

    useEffect(() => {
        getLessonsToProcess()
    }, [getLessonsToProcess])

    const getLessonsToProcessSubtitles = useCallback(async () => {
        setLoadingLessonsSubtitles(true)
        try {
            const lessons = await api.get('/admin/lessons/process/subtitles')
            setLessonsToProcessSubtitles(lessons)
        } catch (error) {
            console.error(error)
        } finally {
            setLoadingLessonsSubtitles(false)
        }
    }, [])

    useEffect(() => {
        getLessonsToProcessSubtitles()
    }, [getLessonsToProcessSubtitles])

    const changeJobFilter = (key, value) => {
        setPage(0)
        setJobFilters(current => ({ ...current, [key]: value,
            ...(key === 'course_id' ? { edition_id: null, module_id: null } : {}),
            ...(key === 'edition_id' ? { module_id: null } : {}),
        }))
        setFilterSearch({})
    }

    useEffect(() => {
        let active = true
        setLoadingFilterOptions(current => ({ ...current, courses: true }))
        api.get('/admin/courses?complete=false').then(items => {
            if (active) setFilterCourses(items || [])
        }).catch(() => { if (active) setFilterError('Impossibile caricare i corsi.') })
            .finally(() => { if (active) setLoadingFilterOptions(current => ({ ...current, courses: false })) })
        return () => { active = false }
    }, [])

    useEffect(() => {
        let active = true
        setFilterEditions([])
        setFilterError('')
        setLoadingFilterOptions(current => ({ ...current, editions: Boolean(jobFilters.course_id) }))
        if (jobFilters.course_id) {
            api.get(`/admin/courses/${jobFilters.course_id}/editions`).then(items => {
                if (active) setFilterEditions(items || [])
            }).catch(() => { if (active) setFilterError('Impossibile caricare le edizioni.') })
                .finally(() => { if (active) setLoadingFilterOptions(current => ({ ...current, editions: false })) })
        }
        return () => { active = false }
    }, [jobFilters.course_id])

    const invalidDateRange = Boolean(jobFilters.from_date && jobFilters.to_date && jobFilters.from_date > jobFilters.to_date)
    const getJobs = useCallback(async ({ showLoader = false } = {}) => {
        if (!showLoader && jobsRequestRef.current && !jobsRequestRef.current.signal.aborted) return
        jobsRequestRef.current?.abort()
        const controller = new AbortController()
        jobsRequestRef.current = controller
        if (invalidDateRange) { setJobs([]); setCount(0); setLoading(false); jobsRequestRef.current = null; return }
        if (showLoader) setLoading(true)
        setJobsError('')
        try {
            const params = new URLSearchParams({ page, per })
            if (jobStatusFilter) params.set('status', jobStatusFilter)
            if (debouncedJobSearch) params.set('q', debouncedJobSearch)
            Object.entries(jobFilters).forEach(([key, value]) => { if (value) params.set(key, value) })
            const result = await api.get(`/admin/recordings-jobs?${params}`, { signal: controller.signal })
            if (controller.signal.aborted) return
            setJobs(result.jobs)
            setCount(result.count)
            setSelectedJob(previous => previous ? result.jobs.find(job => job.id === previous.id) ?? previous : previous)
        } catch (error) {
            if (!controller.signal.aborted) setJobsError('Impossibile caricare i jobs. Riprova.')
        } finally {
            if (!controller.signal.aborted) {
                hasLoadedRef.current = true
                setLoading(false)
                if (jobsRequestRef.current === controller) jobsRequestRef.current = null
            }
        }
    }, [page, per, jobStatusFilter, jobFilters, invalidDateRange, debouncedJobSearch])

    useEffect(() => {
        getJobs({ showLoader: true })
        return () => jobsRequestRef.current?.abort()
    }, [getJobs])

    const getWorkers = useCallback(async ({ showLoader = false } = {}) => {
        if (isFetchingWorkersRef.current) {
            return
        }

        isFetchingWorkersRef.current = true
        if (showLoader) {
            setLoadingWorkers(true)
        }

        try {
            const workers = await api.get('/admin/workers')
            setWorkers(workers)
        } catch (error) {
            console.error(error)
        } finally {
            hasLoadedWorkersRef.current = true
            if (showLoader) {
                setLoadingWorkers(false)
            }
            isFetchingWorkersRef.current = false
        }
    }, [])

    useEffect(() => {
        getWorkers({ showLoader: true })
    }, [getWorkers])

    const hasActiveJobs = useMemo(() => {
        return jobs.some(job => !['completed', 'failed'].includes(job.status))
    }, [jobs])

    const hasActiveWorkers = useMemo(() => {
        return workers.some(worker => worker.status === 'busy')
    }, [workers])

    const hasJobsRequiringWorkersPolling = useMemo(() => {
        return jobs.some(job => ['queued', 'processing', 'failed'].includes(job.status))
    }, [jobs])

    const hasWorkersWithStartedAt = useMemo(() => {
        return workers.some(worker => !!worker.started_at)
    }, [workers])

    useEffect(() => {
        if (!hasLoadedRef.current || !hasActiveJobs) {
            return
        }

        const interval = setInterval(() => {
            getJobs()
        }, 5000)

        return () => clearInterval(interval)
    }, [hasActiveJobs, getJobs])

    useEffect(() => {
        const hasJobsInProgress = jobs.some(job => ['queued', 'processing'].includes(job.status))
        if (!hasLoadedRef.current || !hasJobsInProgress) {
            return
        }

        const interval = setInterval(() => {
            getLessonsToProcess()
            getLessonsToProcessSubtitles()
        }, 10000)

        return () => clearInterval(interval)
    }, [jobs, getLessonsToProcess, getLessonsToProcessSubtitles])

    useEffect(() => {
        if (!hasLoadedWorkersRef.current || !(hasActiveWorkers || hasJobsRequiringWorkersPolling)) {
            return
        }

        const interval = setInterval(() => {
            getWorkers()
        }, 5000)

        return () => clearInterval(interval)
    }, [hasActiveWorkers, hasJobsRequiringWorkersPolling, getWorkers])

    useEffect(() => {
        const shouldTick = (selectedJob && selectedJob.status === 'processing') || hasWorkersWithStartedAt
        if (!shouldTick) {
            return
        }

        const timer = setInterval(() => {
            setNowTs(Date.now())
        }, 1000)

        return () => clearInterval(timer)
    }, [selectedJob, hasWorkersWithStartedAt])

    const JobTypes = [
        {
            id: 'proxy_generation',
            label: 'Generazione Proxy'
        },
        {
            id: 'final_render',
            label: 'Render Finale'
        },
        {
            id: 'subtitle_generation',
            label: 'Generazione Sottotitoli'
        },
        {
            id: 'summary_generation',
            label: 'Sintesi del testo'
        }
    ]

    const getStatusBadge = (status) => {
        switch (status) {
            case 'queued':
                return { name: 'In coda', color: 'var(--sf-gray)' }
            case 'retrying':
                return { name: 'Nuovo tentativo', color: 'var(--sf-orange)' }
            case 'processing':
                return { name: 'In elaborazione', color: 'var(--sf-blue)' }
            case 'completed':
                return { name: 'Completato', color: 'var(--sf-green)' }
            case 'failed':
                return { name: 'Fallito', color: 'var(--sf-red)' }
            case 'cancelled':
                return { name: 'Annullato', color: 'black' }
            case 'paused':
                return { name: 'In pausa', color: 'var(--sf-yellow)' }
            default:
                return { name: status, color: 'var(--sf-gray)' }
        }
    }

    const getPhaseBadge = (phase) => {
        switch (phase) {
            case 'prepare':
                return { name: 'Preparazione', color: 'var(--sf-gray)' }
            case 'encode':
                return { name: 'Compressione', color: 'var(--sf-yellow)' }
            case 'hls':
                return { name: 'Generazione HLS', color: 'var(--sf-orange)' }
            case 'upload':
                return { name: 'Caricamento su Storage', color: 'var(--sf-blue)' }
            case 'audio_extract':
                return { name: 'Estrazione Audio', color: 'var(--sf-yellow)' }
            case 'transcribe':
                return { name: 'Trascrizione', color: 'var(--sf-orange)' }
            case 'summarization':
                return { name: 'Sintesi del testo', color: 'var(--sf-yellow)' }
            case 'chapters_generation':
                return { name: 'Generazione capitoli', color: 'var(--sf-orange)' }
            default:
                return { name: phase ?? '-', color: 'var(--sf-gray)' }
        }
    }

    const getWorkerStatusBadge = (status) => {
        switch (status) {
            case 'busy':
                return { name: 'Occupato', color: 'var(--sf-blue)' }
            case 'paused':
                return { name: 'In pausa', color: 'var(--sf-yellow)' }
            case 'idle':
                return { name: 'Disponibile', color: 'var(--sf-green)' }
            default:
                return { name: status ?? '-', color: 'var(--sf-gray)' }
        }
    }

    const JobPhases = [
        {
            id: 'prepare',
            label: 'Preparazione'
        },
        {
            id: 'encode',
            label: 'Compressione'
        },
        {
            id: 'hls',
            label: 'Generazione HLS'
        },
        {
            id: 'upload',
            label: 'Caricamento su Storage'
        },
        {
            id: 'audio_extract',
            label: 'Estrazione Audio'
        },
        {
            id: 'transcribe',
            label: 'Trascrizione'
        },
        {
            id: 'summarization',
            label: 'Sintesi del testo'
        },
        {
            id: 'chapter_generation',
            label: 'Generazione capitoli'
        }
    ]

    const selectedJobElapsedSeconds = useMemo(() => {
        if (!selectedJob || selectedJob.status !== 'processing' || !selectedJob.started_at) {
            return null
        }
        return Math.max(0, Math.floor((nowTs - new Date(selectedJob.started_at + 'Z').getTime()) / 1000))
    }, [selectedJob, nowTs])

    const selectedJobEstimatedEnd = useMemo(() => {
        if (!selectedJob || selectedJob.status !== 'processing' || selectedJob.eta_seconds == null) {
            return null
        }
        const eta = Math.max(0, Number(selectedJob.eta_seconds))
        return new Date(nowTs + eta * 1000)
    }, [selectedJob, nowTs])

    const isSelectedJobCancelled = selectedJob?.status === 'cancelled'

    const handleCancelJob = useCallback(async (jobId) => {
        try {
            await api.get(`/admin/recordings-jobs/${jobId}/cancel`)
            await getJobs()
        } catch (error) {
            console.error(error)
        }
    }, [getJobs])

    const handleRequeueJob = useCallback(async (jobId) => {
        try {
            await api.get(`/admin/recordings-jobs/${jobId}/requeue`)
            await getJobs()
        } catch (error) {
            console.error(error)
        }
    }, [getJobs])

    const handleRetryNowJob = useCallback(async (jobId) => {
        try {
            await api.get(`/admin/recordings-jobs/${jobId}/retry-now`)
            await getJobs()
        } catch (error) {
            console.error(error)
        }
    }, [getJobs])

    const handleDeleteJob = useCallback(async (jobId) => {
        try {
            await api.delete(`/admin/recordings-jobs/${jobId}`)
            if (selectedJob?.id === jobId) {
                setSelectedJob(null)
            }
            await getJobs()
        } catch (error) {
            console.error(error)
        }
    }, [getJobs, selectedJob])

    const handleGenerateSubtitles = useCallback(async (lesson) => {
        if (!lesson?.id) {
            return
        }

        try {
            await api.post('/admin/recordings-jobs', {
                id: lesson.id,
                lesson_id: lesson.id,
                type: 'subtitle_generation'
            })
            setLessonsToProcessSubtitles((prev) => prev.filter((item) => item.id !== lesson.id))
            await getJobs()
        } catch (error) {
            console.error(error)
        }
    }, [getJobs])

    const handleGenerateAllSubtitles = useCallback(async () => {
        setBulkSubtitleStatus(ButtonStatus.Loading)
        try {
            await api.post('/admin/recordings-jobs/subtitles/bulk')
            await Promise.all([
                getLessonsToProcessSubtitles(),
                getJobs()
            ])
            setBulkSubtitleStatus(ButtonStatus.Success)
            setBulkSubtitleDialogOpen(false)
        } catch (error) {
            console.error(error)
            setBulkSubtitleStatus(ButtonStatus.Error)
        }
    }, [getJobs, getLessonsToProcessSubtitles])

    const handleSkipLessonProcess = useCallback(async (lesson) => {
        if (!lesson?.id) {
            return
        }

        try {
            await api.get(`/admin/lessons/${lesson.id}/skip-process`)
            setLessonsToProcess((prev) => prev.filter((item) => item.id !== lesson.id))
        } catch (error) {
            console.error(error)
        }
    }, [])

    const handleToggleWorkerPause = useCallback(async (worker) => {
        if (!worker?.id) {
            return
        }

        try {
            const updatedWorker = await api.get(`/admin/worker/${worker.id}/pause`)
            setWorkers((prev) => prev.map((item) => item.id === updatedWorker.id ? { ...item, ...updatedWorker } : item))
        } catch (error) {
            console.error(error)
        }
    }, [])

    const closeWorkerActionDialog = useCallback(() => {
        setWorkerActionDialog({ open: false, worker: null })
    }, [])

    const closeSubtitleActionDialog = useCallback(() => {
        setSubtitleActionDialog({ open: false, lesson: null })
    }, [])

    const handleConfirmWorkerAction = useCallback(async () => {
        const { worker } = workerActionDialog
        if (!worker) {
            closeWorkerActionDialog()
            return
        }

        await handleToggleWorkerPause(worker)
        closeWorkerActionDialog()
    }, [workerActionDialog, handleToggleWorkerPause, closeWorkerActionDialog])

    const handleConfirmSubtitleAction = useCallback(async () => {
        const { lesson } = subtitleActionDialog
        if (!lesson) {
            closeSubtitleActionDialog()
            return
        }

        await handleGenerateSubtitles(lesson)
        closeSubtitleActionDialog()
    }, [subtitleActionDialog, handleGenerateSubtitles, closeSubtitleActionDialog])

    const handleConfirmJobAction = useCallback(async () => {
        const { job, type } = jobActionDialog
        if (!job || !type) {
            setJobActionDialog({ open: false, type: null, job: null })
            return
        }

        if (type === 'cancel') {
            await handleCancelJob(job.id)
        }

        if (type === 'requeue') {
            await handleRequeueJob(job.id)
        }

        if (type === 'retry-now') {
            await handleRetryNowJob(job.id)
        }

        if (type === 'delete') {
            await handleDeleteJob(job.id)
        }

        setJobActionDialog({ open: false, type: null, job: null })
    }, [jobActionDialog, handleCancelJob, handleRequeueJob, handleRetryNowJob, handleDeleteJob])

    const closeJobActionDialog = useCallback(() => {
        setJobActionDialog({ open: false, type: null, job: null })
    }, [])

    const getJobActions = useCallback((job) => {
        if (!job) {
            return []
        }

        if (job.status === 'processing') {
            return [
                {
                    label: 'Annulla',
                    icon: CancelIcon,
                    onClick: () => setJobActionDialog({ open: true, type: 'cancel', job }),
                    style: { color: 'var(--sf-red)' }
                }
            ]
        }

        if (job.status === 'failed') {
            return [
                {
                    label: 'Ritenta ora',
                    icon: RetryIcon,
                    onClick: () => setJobActionDialog({ open: true, type: 'retry-now', job })
                },
                {
                    label: 'Annulla',
                    icon: CancelIcon,
                    onClick: () => setJobActionDialog({ open: true, type: 'cancel', job }),
                },
                {
                    label: 'Elimina',
                    icon: DeleteIcon,
                    onClick: () => setJobActionDialog({ open: true, type: 'delete', job }),
                    style: { color: 'var(--sf-red)' }
                }
            ]
        }

        return [
            {
                label: 'Rimetti in coda',
                icon: RetryIcon,
                onClick: () => setJobActionDialog({ open: true, type: 'requeue', job })
            },
            {
                label: 'Elimina',
                icon: DeleteIcon,
                onClick: () => setJobActionDialog({ open: true, type: 'delete', job }),
                style: { color: 'var(--sf-red)' }
            }
        ]
    }, [])

    const getWorkerActions = useCallback((worker) => {
        if (!worker) {
            return []
        }

        const isPaused = worker.status === 'paused'

        return [
            {
                label: isPaused ? 'Riattiva' : 'Metti in pausa',
                icon: isPaused ? PlayIcon : PauseIcon,
                onClick: () => setWorkerActionDialog({ open: true, worker })
            }
        ]
    }, [])

    const getSubtitleActions = useCallback((lesson) => {
        if (!lesson) {
            return []
        }

        return [
            {
                label: 'Genera sottotitoli',
                icon: SubtitlesIcon,
                onClick: () => setSubtitleActionDialog({ open: true, lesson })
            }
        ]
    }, [])

    const getLessonToProcessActions = useCallback((lesson) => {
        if (!lesson) {
            return []
        }

        return [
            {
                label: 'Salta processamento',
                icon: CancelIcon,
                onClick: () => handleSkipLessonProcess(lesson),
                style: { color: 'var(--sf-red)' }
            }
        ]
    }, [handleSkipLessonProcess])

    return (
        <HeaderFooterLayout>
            <HelmetProvider><Helmet><title>Processamenti - Jobs</title></Helmet></HelmetProvider>
            <div className={styles.container}>
                <div className={styles.section}>
                    <div className={styles.sectionInner}>
                        <header className={styles.heading}><h1>Processamenti</h1><p>Monitora i jobs, lo stato dei workers e le lezioni pronte per l’elaborazione.</p></header>
                        <nav className={styles.sectionNav} aria-label="Sezioni processamenti">
                            <a href="#jobs-lessons">Lezioni da processare</a><a href="#jobs-list">Jobs</a><a href="#jobs-workers">Workers</a><a href="#jobs-subtitles">Sottotitoli</a>
                        </nav>
                        <section id="jobs-lessons" className={styles.reportSection}><Card appearance="modern" title="Lezioni da processare" integratedTable>
                            <Table appearance="minimal"
                                loading={loadingLessons}
                                loadingN={5}
                                style={{
                                    borderRadius: 0
                                }}
                                columns={[
                                    { key: 'id', label: 'ID', type: 'number' },
                                    { key: 'room_name', label: 'STANZA', type: 'string' },
                                    { key: 'name', label: 'NOME', type: 'string' },
                                    { key: 'starts_at', label: 'ORARIO INIZIO', type: 'datetime' },
                                    { key: 'ended_at', label: 'ORARIO FINE', type: 'datetime' },
                                    { key: 'course_name', label: 'CORSO', type: 'string' },
                                    { key: 'edition_name', label: 'EDIZIONE', type: 'string' },
                                    { key: '_actions', label: '', type: 'custom', handleClick: true, style: { width: 52 } },
                                ]}
                                elements={lessonsToProcess.map(lesson => ({
                                    ...lesson,
                                    edition_name: lesson.edition?.name,
                                    course_name: lesson.course?.name,
                                    href: `/lessons/${lesson.id}/recordings/editor`,
                                    _actions: {
                                        value: lesson.id,
                                        type: 'number',
                                        component: <MeatBallsMenu actions={getLessonToProcessActions(lesson)} />
                                    },
                                }))}
                            />
                            {!loadingLessons && lessonsToProcess.length === 0 && <div className={styles.empty}>Nessuna lezione in attesa di processamento.</div>}
                        </Card>
                        </section>
                        <section id="jobs-list" className={styles.reportSection}><Card appearance="modern" title="Jobs" integratedTable actions={<span className={styles.count}>{loading ? "Caricamento…" : `${count.toLocaleString("it-IT")} jobs`}</span>}>
                            <div className={styles.searchToolbar}>
                                <TextInput appearance="modern" type="search" value={jobSearch} onKeyUp={value => setJobSearch(value || '')} placeholder="Cerca per ID, lezione, corso, edizione o modulo…" />
                                <button type="button" className={styles.filterButton} aria-expanded={filtersOpen} aria-controls="job-filters" onClick={() => setFiltersOpen(open => !open)}>
                                    <FilterIcon aria-hidden="true" />Filtri{(Object.values(jobFilters).filter(Boolean).length + Number(Boolean(jobStatusFilter))) > 0 ? ` (${Object.values(jobFilters).filter(Boolean).length + Number(Boolean(jobStatusFilter))})` : ''}
                                </button>
                            </div>
                            <div id="job-filters" className={styles.jobsFilters} hidden={!filtersOpen}>
                                <div className={styles.jobsFilterSelect}><span className={styles.filterLabel}>Stato del job</span>
                                    <Select appearance="modern" portal
                                        placeholder="Tutti gli stati"
                                        options={jobStatusOptions}
                                        selected={jobStatusOptions.find(option => option.value === jobStatusFilter)}
                                        nullable
                                        nullLabel="Tutti gli stati"
                                        onSelect={(option) => {
                                            setPage(0)
                                            setJobStatusFilter(option?.id === -1 ? null : option?.value ?? null)
                                        }}
                                    />
                                </div>
                                {[
                                    ['course_id', 'Corso', filterCourses, loadingFilterOptions.courses, false],
                                    ['edition_id', 'Edizione', filterEditions, loadingFilterOptions.editions, !jobFilters.course_id],
                                    ['module_id', 'Modulo', filterEditions.find(item => item.id === jobFilters.edition_id)?.modules || [], false, !jobFilters.edition_id],
                                ].map(([key, label, items, isLoading, disabled]) => <div className={styles.jobsFilterSelect} key={key}>
                                    <span className={styles.filterLabel}>{label}</span>
                                    <Select appearance="modern" portal nullable nullLabel={key === "course_id" ? "Tutti i corsi" : key === "edition_id" ? "Tutte le edizioni" : "Tutti i moduli"}
                                        placeholder={isLoading ? 'Caricamento…' : key === 'course_id' ? 'Tutti i corsi' : key === 'edition_id' ? 'Tutte le edizioni' : 'Tutti i moduli'}
                                        disabled={disabled || isLoading} selected={items.find(item => item.id === jobFilters[key]) || null}
                                        options={[...items].sort((a, b) => a.name.localeCompare(b.name, 'it')).filter(item => item.name.toLowerCase().includes((filterSearch[key] || '').toLowerCase()))}
                                        searchPlaceholder={`Cerca ${label.toLowerCase()}`} onSearch={value => setFilterSearch(current => ({ ...current, [key]: value || '' }))}
                                        onSelect={item => changeJobFilter(key, item?.id === -1 ? null : item?.id ?? null)} />
                                </div>)}
                                <label className={styles.dateField}><span className={styles.filterLabel}>Creato dal</span><input type="date" value={jobFilters.from_date} max={jobFilters.to_date || undefined} onChange={event => changeJobFilter('from_date', event.target.value)} /></label>
                                <label className={styles.dateField}><span className={styles.filterLabel}>Creato al</span><input type="date" value={jobFilters.to_date} min={jobFilters.from_date || undefined} onChange={event => changeJobFilter('to_date', event.target.value)} /></label>
                                <div className={styles.filterFooter}><span className={styles.filterHint}>Date di creazione del job, estremi inclusi (ora italiana).</span>
                                    <Button appearance="subtle" onClick={() => { setPage(0); setJobStatusFilter(null); setJobFilters({ from_date: '', to_date: '', course_id: null, edition_id: null, module_id: null }); setFilterSearch({}) }}>Azzera filtri</Button>
                                </div>
                            </div>
                            {(invalidDateRange || jobsError || filterError) && <div role="alert" className={styles.filterError}>{invalidDateRange ? 'La data iniziale deve precedere o coincidere con quella finale.' : jobsError || filterError}{jobsError && <Button appearance="subtle" onClick={() => getJobs({ showLoader: true })}>Riprova</Button>}</div>}
                            <Table appearance="minimal"
                                onRowClick={(item) => { setSelectedJob(item) }}
                                loading={loading}
                                loadingN={8}
                                style={{
                                    borderRadius: 0
                                }}
                                columns={[
                                    { key: 'id', label: 'ID', type: 'number' },
                                    { key: '_lesson', label: 'LEZIONE', type: 'custom' },
                                    { key: '_course', label: 'CORSO', type: 'custom' },
                                    { key: '_type', label: 'TIPO', type: 'string' },
                                    { key: '_progress', label: 'PROGRESSO', type: 'custom' },
                                    { key: 'created_at', label: 'CREATO IL', type: 'datetime' },
                                    { key: '_status', label: 'STATO', type: 'custom' },
                                    { key: '_actions', label: '', type: 'custom', handleClick: true, style: { width: 52 } },
                                ]}
                                elements={jobs.map(job => ({
                                    ...job,
                                    _current_phase: JobPhases.find(p => p.id === job.current_phase)?.label ?? job.current_phase,
                                    _type: JobTypes.find(t => t.id === job.type)?.label ?? job.type,
                                    _status: { value: job.status, type: 'string', component: <JobBadge {...getStatusBadge(job.status)} /> },
                                    _progress: {
                                        value: job.progress,
                                        type: 'string',
                                        component: (
                                            <div className={styles.progressCell}><span>{Math.min(100, Math.max(0, Number(job.progress) || 0))}%</span><ProgressBar progress={Math.min(100, Math.max(0, Number(job.progress) || 0))} /></div>
                                        )
                                    },
                                    _eta: { value: job.eta, type: 'string', component: <div>{formatSeconds(job.eta_seconds)}</div> },
                                    _lesson: {
                                        value: job.lesson?.name, type: 'string', component: <div className={styles.lesson}>
                                            <div className={styles.lessonName}>{job.lesson?.name}</div>
                                            <div className={typo.caption}><span style={{ color: 'var(--secondary)' }}>{job.lesson?.room_name}</span> • {formatDateV2(job.lesson?.starts_at, { day: '2-digit', month: '2-digit', year: 'numeric' })} - {formatTimeV2(job.lesson?.starts_at)}</div>
                                        </div>
                                    },
                                    _course: {
                                        value: job.lesson?.course?.name, type: 'string', component: <div>
                                            <div className={styles.lessonName}>{job.lesson?.course?.name}</div>
                                            <div className={`${typo.caption} ${styles.lessonInfo}`}>{job.lesson?.edition?.name}</div>
                                        </div>
                                    },
                                    _actions: {
                                        value: job.id,
                                        type: 'number',
                                        component: <MeatBallsMenu actions={getJobActions(job)} />
                                    },
                                }))}
                            />
                            {!loading && jobs.length === 0 && <div className={styles.empty}>Nessun job per lo stato selezionato.</div>}
                            <div className={styles.paginationFooter}><Pagination group={width > 540 ? 5 : 0} total={count} per={per} page={page} onChange={p => setPage(p)} loading={loading} /></div>
                        </Card>

                        </section><section id="jobs-workers" className={styles.reportSection}><Card appearance="modern" title="Workers" integratedTable>
                            <Table appearance="minimal"
                                loading={loadingWorkers}
                                loadingN={1}
                                style={{
                                    borderRadius: 0
                                }}
                                columns={[
                                    { key: 'id', label: 'ID', type: 'number' },
                                    { key: 'name', label: 'NOME', type: 'string' },
                                    { key: '_status', label: 'STATO', type: 'custom' },
                                    { key: '_cache_dir', label: 'DIMENSIONE CACHE', type: 'custom' },
                                    { key: '_uptime', label: 'UPTIME', type: 'custom' },
                                    { key: 'current_job_id', label: 'JOB CORRENTE', type: 'number' },
                                    { key: 'last_heartbeat_at', label: 'ULTIMO HEARTBEAT', type: 'datetime' },
                                    { key: 'started_at', label: 'AVVIATO IL', type: 'datetime' },
                                    { key: 'created_at', label: 'CREATO IL', type: 'datetime' },
                                    { key: 'updated_at', label: 'AGGIORNATO IL', type: 'datetime' },
                                    { key: '_actions', label: '', type: 'custom', handleClick: true, style: { width: 52 } },
                                ]}
                                elements={workers.map(worker => ({
                                    ...worker,
                                    _status: { value: worker.status, type: 'string', component: <JobBadge {...getWorkerStatusBadge(worker.status)} /> },
                                    _cache_dir: {
                                        value: Number(worker.cache_size ?? 0),
                                        type: 'string',
                                        component: <div>{formatFileSize(Number(worker.cache_size ?? 0))}</div>
                                    },
                                    _uptime: {
                                        value: worker.started_at,
                                        type: 'string',
                                        component: (
                                            <div>
                                                {worker.started_at
                                                    ? formatSecondsDuration(
                                                        Math.max(
                                                            0,
                                                            Math.floor((nowTs - new Date(worker.started_at + 'Z').getTime()) / 1000)
                                                        )
                                                    )
                                                    : '-'}
                                            </div>
                                        )
                                    },
                                    _actions: {
                                        value: worker.id,
                                        type: 'number',
                                        component: <MeatBallsMenu actions={getWorkerActions(worker)} />
                                    }
                                }))}
                            />
                        </Card>
                        </section>
                        <section id="jobs-subtitles" className={styles.reportSection}>
                        <Card appearance="modern" title="Lezioni senza sottotitoli" integratedTable actions={
                            <Button appearance="subtle" status={bulkSubtitleStatus}
                                disabled={loadingLessonsSubtitles || lessonsToProcessSubtitles.length === 0 || bulkSubtitleStatus === ButtonStatus.Loading}
                                onClick={() => setBulkSubtitleDialogOpen(true)}><SubtitlesIcon style={{ width: 14, height: 14 }} />Genera tutti</Button>
                        }>
                            <div className={styles.subtitlesTable}>
                                <Table appearance="minimal"
                                    loading={loadingLessonsSubtitles}
                                    loadingN={5}
                                    style={{
                                        borderRadius: 0
                                    }}
                                    columns={[
                                        { key: 'id', label: 'ID', type: 'number' },
                                        { key: '_lesson', label: 'LEZIONE', type: 'custom' },
                                        { key: 'starts_at', label: 'ORARIO INIZIO', type: 'datetime' },
                                        { key: 'ended_at', label: 'ORARIO FINE', type: 'datetime' },
                                        { key: '_course', label: 'CORSO', type: 'custom' },
                                        { key: '_actions', label: '', type: 'custom', handleClick: true, style: { width: 52 } },
                                    ]}
                                    elements={lessonsToProcessSubtitles.map(lesson => ({
                                        ...lesson,
                                        edition_name: lesson.edition?.name,
                                        course_name: lesson.course?.name,
                                        _lesson: {
                                            value: lesson?.name, type: 'string', component: <div className={styles.lesson}>
                                                <div className={styles.lessonName}>{lesson?.name}</div>
                                                <div className={typo.caption}><span style={{ color: 'var(--secondary)' }}>{lesson?.room_name}</span></div>
                                            </div>
                                        },
                                        _course: {
                                            value: lesson?.course?.name, type: 'string', component: <div>
                                                <div className={styles.lessonName}>{lesson?.course?.name}</div>
                                                <div className={`${typo.caption} ${styles.lessonInfo}`}>{lesson?.edition?.name}</div>
                                            </div>
                                        },
                                        _actions: {
                                            value: lesson.id,
                                            type: 'number',
                                            component: <MeatBallsMenu actions={getSubtitleActions(lesson)} />
                                        }
                                    }))}
                                />
                                {!loadingLessonsSubtitles && lessonsToProcessSubtitles.length === 0 && <div className={styles.empty}>Nessuna lezione richiede la generazione dei sottotitoli.</div>}
                            </div>
                        </Card>
                        </section>
                    </div>
                </div>
            </div>
            {selectedJob &&
                <Dialog appearance="modern" open={!!selectedJob} title={`Job #${selectedJob.id}`} onClose={() => setSelectedJob(null)} style={{ width: '960px', maxWidth: 'calc(100vw - 32px)' }} contentMaxHeight="80vh">
                    <div className={styles.detailHeading}><h2>{selectedJob.lesson?.name || 'Lezione non disponibile'}</h2><p>{[selectedJob.lesson?.course?.name, selectedJob.lesson?.edition?.name].filter(Boolean).join(' · ')}</p></div>
                    <div className={styles.infoGrid}>
                        <div className={styles.infoCard}>
                            <div className={styles.infoLabel}>Tipo</div>
                            <div className={styles.infoValue}>{JobTypes.find(t => t.id === selectedJob.type)?.label ?? selectedJob.type}</div>
                        </div>
                        <div className={styles.infoCard}>
                            <div className={styles.infoLabel}>Stato</div>
                            <JobBadge {...getStatusBadge(selectedJob.status)} size='small' />
                        </div>
                        {!isSelectedJobCancelled && selectedJob.status !== 'completed' && (
                            <div className={styles.infoCard}>
                                <div className={styles.infoLabel}>Progresso</div>
                                <div className={styles.infoValue}>{selectedJob.progress}%</div>
                                <ProgressBar progress={selectedJob.progress ?? 0} />
                            </div>
                        )}
                        {selectedJob.status === 'processing' && !isSelectedJobCancelled && (
                            <div className={styles.infoCard}>
                                <div className={styles.infoLabel}>Fase corrente</div>
                                <JobBadge {...getPhaseBadge(selectedJob.current_phase)} size='small' />
                            </div>
                        )}
                        <div className={styles.infoCard}>
                            <div className={styles.infoLabel}>Creato il</div>
                            <div className={styles.infoValue}>{formatDateV2(selectedJob.created_at, { day: '2-digit', month: '2-digit', year: 'numeric' })} - {formatTimeV2(selectedJob.created_at)}</div>
                        </div>
                        {isSelectedJobCancelled && (
                            <div className={styles.infoCard}>
                                <div className={styles.infoLabel}>Iniziato il</div>
                                <div className={styles.infoValue}>
                                    {selectedJob.started_at
                                        ? `${formatDateV2(selectedJob.started_at, { day: '2-digit', month: '2-digit', year: 'numeric' })} - ${formatTimeV2(selectedJob.started_at)}`
                                        : '-'}
                                </div>
                            </div>
                        )}
                        {isSelectedJobCancelled && (
                            <div className={styles.infoCard}>
                                <div className={styles.infoLabel}>Annullato il</div>
                                <div className={styles.infoValue}>
                                    {selectedJob.updated_at
                                        ? `${formatDateV2(selectedJob.updated_at, { day: '2-digit', month: '2-digit', year: 'numeric' })} - ${formatTimeV2(selectedJob.updated_at)}`
                                        : '-'}
                                </div>
                            </div>
                        )}
                        {!isSelectedJobCancelled && selectedJob.started_at && (selectedJob.status === 'processing' || selectedJob.completed_at) && (
                            <div className={styles.infoCard}>
                                <div className={styles.infoLabel}>Iniziato il</div>
                                <div className={styles.infoValue}>{formatDateV2(selectedJob.started_at, { day: '2-digit', month: '2-digit', year: 'numeric' })} - {formatTimeV2(selectedJob.started_at)}</div>
                            </div>
                        )}
                        {!isSelectedJobCancelled && !selectedJob.completed_at &&
                            <div className={styles.infoCard}>
                                <div className={styles.infoLabel}>Tempo stimato</div>
                                <div className={styles.infoValue}>{formatSeconds(selectedJob.eta_seconds)}</div>
                            </div>
                        }
                        {!isSelectedJobCancelled && selectedJob.status === 'processing' && selectedJobEstimatedEnd && (
                            <div className={styles.infoCard}>
                                <div className={styles.infoLabel}>Fine stimata</div>
                                <div className={styles.infoValue}>{formatDateV2(selectedJobEstimatedEnd.toISOString(), { day: '2-digit', month: '2-digit', year: 'numeric' }, "it", false)} - {formatTimeV2(selectedJobEstimatedEnd.toISOString(), undefined, "it", false)}</div>
                            </div>
                        )}
                        {!isSelectedJobCancelled && selectedJob.status === 'processing' && selectedJobElapsedSeconds != null && (
                            <div className={styles.infoCard}>
                                <div className={styles.infoLabel}>Tempo trascorso</div>
                                <div className={styles.infoValue}>{formatSecondsDuration(selectedJobElapsedSeconds)}</div>
                            </div>
                        )}
                        {!isSelectedJobCancelled && selectedJob.completed_at &&
                            <>
                                <div className={styles.infoCard}>
                                    <div className={styles.infoLabel}>Completato il</div>
                                    <div className={styles.infoValue}>{formatDateV2(selectedJob.completed_at, { day: '2-digit', month: '2-digit', year: 'numeric' })} - {formatTimeV2(selectedJob.completed_at)}</div>
                                </div>
                                <div className={styles.infoCard}>
                                    <div className={styles.infoLabel}>Completato in</div>
                                    <div className={styles.infoValue}>{formatSeconds((new Date(selectedJob.completed_at) - new Date(selectedJob.started_at)) / 1000)}</div>
                                </div>
                            </>
                        }
                        {!isSelectedJobCancelled && selectedJob.status === 'failed' &&
                            <>
                                <div className={styles.infoCard}>
                                    <div className={styles.infoLabel}>Prossimo Tentativo</div>
                                    <div className={styles.infoValue}>{formatDateV2(selectedJob.next_retry_at, { day: '2-digit', month: '2-digit', year: 'numeric' })} - {formatTimeV2(selectedJob.next_retry_at)}</div>
                                </div>
                                <div className={`${styles.infoCard} ${styles.infoCardWide}`}>
                                    <div className={styles.infoLabel}>Errore</div>
                                    <div className={styles.infoValue}>{selectedJob.error_message}</div>
                                </div>
                            </>
                        }
                        {(selectedJob.status === 'processing' || selectedJob.status === 'failed') && (
                            <div className={styles.infoCard}>
                                <div className={styles.infoLabel}>Tentativi</div>
                                <div className={styles.infoValue}>
                                    <strong>{Number(selectedJob.attempts ?? 0)}</strong>/{selectedJob.max_attempts ?? '-'}
                                </div>
                            </div>
                        )}
                    </div>
                    <div className={styles.logsHeader}><h3>Log di elaborazione</h3><Button appearance="subtle" onClick={() => {
                        document.getElementById('logs-end')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
                    }}><ArrowIcon style={{ width: 12, height: 12 }} />Vai alla fine</Button></div>
                    <div className={styles.logs} id="logs">
                        {selectedJob.logs ? <LogViewer logText={selectedJob.logs} /> : "Nessun log disponibile"}
                        <div id="logs-end" />
                    </div>
                </Dialog>
            }
            <AlertDialog dialogProps={{ appearance: "modern" }}
                open={jobActionDialog.open}
                title={
                    jobActionDialog.type === 'cancel'
                        ? "Annullare il processamento?"
                        : jobActionDialog.type === 'retry-now'
                            ? "Ritentare ora il processamento fallito?"
                            : jobActionDialog.type === 'delete'
                                ? "Eliminare il job?"
                                : "Rimettere in coda il processamento?"
                }
                text={
                    jobActionDialog.type === 'cancel'
                        ? "Sei sicuro di voler annullare questo recording job in corso?"
                        : jobActionDialog.type === 'retry-now'
                            ? "Sei sicuro di voler anticipare il retry di questo recording job fallito?"
                            : jobActionDialog.type === 'delete'
                                ? "Sei sicuro di voler eliminare questo recording job? Questa azione non può essere annullata."
                                : "Sei sicuro di voler rimettere in coda questo recording job?"
                }
                onClose={closeJobActionDialog}
                actions={[
                    {
                        label: 'ANNULLA',
                        accentColor: jobActionDialog.type === 'delete' ? 'var(--sf-red)' : 'var(--sf-red)',
                        inverse: true,
                        onClick: async () => {
                            closeJobActionDialog()
                        }
                    },
                    {
                        label: 'CONFERMA',
                        accentColor: (jobActionDialog.type === 'cancel' || jobActionDialog.type === 'delete') ? 'var(--sf-red)' : 'var(--primary)',
                        onClick: handleConfirmJobAction
                    }
                ]}
            />
            <AlertDialog dialogProps={{ appearance: "modern" }}
                open={workerActionDialog.open}
                title={workerActionDialog.worker?.status === 'paused' ? "Riattivare il worker?" : "Mettere in pausa il worker?"}
                text={workerActionDialog.worker?.status === 'paused'
                    ? "Sei sicuro di voler riattivare questo worker?"
                    : "Sei sicuro di voler mettere in pausa questo worker? Se ci sono job in corso questi saranno comunque completati prima di mettere in pausa il worker."}
                onClose={closeWorkerActionDialog}
                actions={[
                    {
                        label: 'ANNULLA',
                        inverse: true,
                        onClick: async () => {
                            closeWorkerActionDialog()
                        }
                    },
                    {
                        label: 'CONFERMA',
                        onClick: handleConfirmWorkerAction
                    }
                ]}
            />
            <AlertDialog dialogProps={{ appearance: "modern" }}
                open={subtitleActionDialog.open}
                title="Generare i sottotitoli?"
                text="Sei sicuro di voler generare i sottotitoli per questa lezione?"
                onClose={closeSubtitleActionDialog}
                actions={[
                    {
                        label: 'ANNULLA',
                        inverse: true,
                        onClick: async () => {
                            closeSubtitleActionDialog()
                        }
                    },
                    {
                        label: 'CONFERMA',
                        onClick: handleConfirmSubtitleAction
                    }
                ]}
            />
            <AlertDialog dialogProps={{ appearance: "modern" }}
                open={bulkSubtitleDialogOpen}
                title="Generare tutti i sottotitoli?"
                text={`Verranno creati i job per tutte le ${lessonsToProcessSubtitles.length} lezioni che richiedono sottotitoli.`}
                onClose={() => setBulkSubtitleDialogOpen(false)}
                actions={[
                    {
                        label: 'ANNULLA',
                        inverse: true,
                        onClick: async () => {
                            setBulkSubtitleDialogOpen(false)
                        }
                    },
                    {
                        label: 'CONFERMA',
                        onClick: handleGenerateAllSubtitles
                    }
                ]}
            />
        </HeaderFooterLayout>
    )

}

export default RecordingsJobs


function LogViewer({ logText }) {
    const lines = logText.split("\n");

    function parseLogLine(line) {
        // match: [2026-02-19 19:05:06] [FINAL] Message
        const regex = /^\[(.*?)\]\s*(\[(.*?)\])?\s*(.*)$/;
        const match = line.match(regex);

        if (!match) return null;

        return {
            timestamp: match[1],
            tag: match[3] || null,
            message: match[4]
        };
    }

    const tagColors = {
        FINAL: "#3b82f6",   // blu
        CACHE: "#10b981",   // verde
        ERROR: "#ef4444",   // rosso
        WARNING: "#f59e0b", // arancione
    };

    return (
        <div className={styles.logLines}>
            {lines.map((line, index) => {
                const parsed = parseLogLine(line);
                if (!parsed) return <div key={index}>{line}</div>;

                return (
                    <div key={index}>
                        <span style={{ color: "#94a3b8" }}>
                            [{parsed.timestamp}]
                        </span>{" "}

                        {parsed.tag && (
                            <span
                                style={{
                                    color: tagColors[parsed.tag] || "#c4b5fd",
                                    fontWeight: "bold",
                                }}
                            >
                                [{parsed.tag}]
                            </span>
                        )}{" "}

                        <span>{parsed.message}</span>
                    </div>
                );
            })}
        </div>
    );
}
